import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { triggerChat } from '@/utils/chatTrigger';

const Pricing = () => {
  const commonFeatures = [
    "1-hour onboarding call to understand your business",
    "Custom-branded website widget",
    "Custom welcome video for every visitor",
    "Live agent on standby on your booking page",
    "Appointments booked directly into your calendar",
    "$25 per appointment booked"
  ];

  const plans = [
    {
      months: "3-MONTH",
      title: "STANDARD ENTRY",
      price: "$2,500",
      totalLabel: "total",
      accent: false,
      tag: "Getting Started"
    },
    {
      months: "6-MONTH",
      title: "COMMIT & SAVE",
      price: "$4,000",
      totalLabel: "total",
      accent: false,
      tag: "Most Popular"
    },
    {
      months: "12-MONTH",
      title: "BEST VALUE",
      price: "$7,000",
      totalLabel: "total",
      accent: true,
      tag: "Best Value"
    }
  ];

  return (
    <section id="pricing" className="py-24 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gold/5 blur-[150px] pointer-events-none rounded-full transform translate-x-1/2"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-anton uppercase tracking-wide text-white mb-6">
            Select Your <span className="text-gold">Retainer</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto font-medium">
            Professional live agents on standby to turn your visitors into booked appointments.
          </p>
        </motion.div>

        {/* Global Features List */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          className="bg-white/[0.02] border border-white/5 rounded-3xl p-8 md:p-12 mb-12"
        >
          <div className="text-center mb-10">
            <h4 className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-2">Standard Inclusions</h4>
            <h3 className="text-2xl md:text-3xl font-anton uppercase text-white">Included in Every Package</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commonFeatures.map((feature, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors group">
                <div className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0 group-hover:bg-gold/20 transition-colors">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-gold" />
                </div>
                <p className="text-gray-300 font-medium leading-tight pt-2">{feature}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Updated: Full Width Availability Banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="w-full mb-8"
        >
          <div className="bg-gold rounded-2xl p-6 md:p-8 shadow-[0_0_50px_rgba(245,166,35,0.15)] border-2 border-white/20 flex flex-col md:flex-row items-center justify-between gap-6 text-navy relative overflow-hidden group">
            {/* Decorative Pulse Background */}
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
              <div className="w-16 h-16 rounded-full bg-navy/10 flex items-center justify-center shrink-0 border-2 border-navy/20">
                <SafeIcon icon={FiIcons.FiClock} className="text-3xl animate-pulse" />
              </div>
              <div className="text-center md:text-left">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-70 mb-1">Current Service Window</p>
                <h4 className="text-2xl md:text-4xl font-anton uppercase tracking-wide leading-none">
                  Live Agent Coverage: 9:00 AM – 6:00 PM CST (Weekdays)
                </h4>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-4 relative z-10">
              <div className="text-right">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-70">Status</p>
                <p className="text-lg font-anton uppercase">Ready to Assist</p>
              </div>
              <div className="w-3 h-3 rounded-full bg-navy animate-ping"></div>
            </div>
          </div>
        </motion.div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {plans.map((plan, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className={`relative flex flex-col h-full rounded-2xl border ${
                plan.accent 
                  ? 'bg-gradient-to-b from-navy-light to-navy border-gold shadow-[0_0_40px_rgba(245,166,35,0.15)] scale-105 z-10' 
                  : 'bg-navy-lighter border-white/10 shadow-xl'
              } p-8`}
            >
              {plan.tag && (
                <div className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-lg ${
                  plan.accent ? 'bg-gold text-navy' : 'bg-white/10 text-gray-300 border border-white/10'
                }`}>
                  {plan.tag}
                </div>
              )}

              <div className="mb-8">
                <span className="text-gold font-anton tracking-widest text-lg block mb-1">{plan.months}</span>
                <h3 className="text-2xl font-anton uppercase text-white mb-6">{plan.title}</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-anton text-white">{plan.price}</span>
                  <span className="text-gray-400 font-bold uppercase text-xs tracking-widest">{plan.totalLabel}</span>
                </div>
              </div>

              <div className="space-y-4 mb-10 flex-grow pt-6 border-t border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                    <SafeIcon icon={FiIcons.FiCheck} className="text-gold text-sm" />
                  </div>
                  <span className="text-gray-300 font-bold">$25 Per Appointment</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                    <SafeIcon icon={FiIcons.FiCheck} className="text-gold text-sm" />
                  </div>
                  <span className="text-gray-300 font-medium">Dedicated Live Agent</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                    <SafeIcon icon={FiIcons.FiCheck} className="text-gold text-sm" />
                  </div>
                  <span className="text-gray-300 font-medium">All Core Features</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                    <SafeIcon icon={FiIcons.FiCheck} className="text-gold text-sm" />
                  </div>
                  <span className="text-gray-300 font-medium">Instant Lead Capture</span>
                </div>
              </div>

              <button 
                onClick={triggerChat}
                className={`w-full py-4 text-center rounded-md font-bold uppercase tracking-widest transition-all active:scale-95 ${
                  plan.accent 
                    ? 'bg-gold text-navy hover:bg-gold-hover hover:shadow-[0_0_20px_rgba(245,166,35,0.4)]' 
                    : 'border-2 border-white/20 text-white hover:bg-white/5'
                }`}
              >
                Get Started
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;